import React, { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { loadLogoForPDF, addLogoToPDF } from '../pdfLogoUtil';

const NoShowPDF = ({ chartData, chartRef, user }) => {
  const [generating, setGenerating] = useState(false);

  const generatePDF = async () => {
    setGenerating(true);

    try {
      const pdf = new jsPDF('l', 'mm', 'letter');
      const userName = user?.name || 'Unknown User';
      const timestamp = new Date().toLocaleString();

      const logoDataUrl = await loadLogoForPDF();
      const addLogo = (x, yPos, w, h) => {
        addLogoToPDF(pdf, logoDataUrl, x, yPos, w, h);
      };

      const {
        totalClients, totalAppointments, totalNoShows,
        practiceNoShowRate, attendanceRate,
        criticalCount, criticalPct, highRiskCount, highRiskPct, moderateRiskCount, lowRiskCount,
        topNoShowClients
      } = chartData;

      // ============================================================
      // PAGE 1: Chart (landscape)
      // ============================================================
      const lPageWidth = pdf.internal.pageSize.getWidth();
      const lPageHeight = pdf.internal.pageSize.getHeight();
      const lMargin = 15;
      let y = lMargin;

      const headerLogoH = 16;
      const headerLogoW = headerLogoH * 3;
      addLogo(lPageWidth - lMargin - headerLogoW, y, headerLogoW, headerLogoH);

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('No-Show Percentage Analysis', lMargin, y + 6);

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, lMargin, y + 12);
      y += headerLogoH + 4;

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(lMargin, y, lPageWidth - lMargin, y);
      y += 3;

      // Chart image
      if (chartRef?.current) {
        try {
          const canvas = await html2canvas(chartRef.current, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true
          });
          const chartImgData = canvas.toDataURL('image/png');

          const footerReserve = 32;
          const availableHeight = lPageHeight - y - footerReserve;
          const availableWidth = lPageWidth - lMargin * 2;

          const aspectRatio = canvas.height / canvas.width;
          let imgWidth = availableWidth;
          let imgHeight = imgWidth * aspectRatio;

          if (imgHeight > availableHeight) {
            imgHeight = availableHeight;
            imgWidth = imgHeight / aspectRatio;
          }

          const imgX = lMargin + (availableWidth - imgWidth) / 2;
          pdf.addImage(chartImgData, 'PNG', imgX, y, imgWidth, imgHeight);
        } catch (e) {
          console.error('Chart capture failed:', e);
        }
      }

      // ============================================================
      // PAGE 2: Analytics (portrait)
      // ============================================================
      pdf.addPage('letter', 'portrait');
      const aPageWidth = pdf.internal.pageSize.getWidth();
      const aPageHeight = pdf.internal.pageSize.getHeight();
      const aMargin = 20;
      y = aMargin;

      const aLogoH = 14;
      const aLogoW = aLogoH * 3;
      addLogo(aPageWidth - aMargin - aLogoW, y, aLogoW, aLogoH);

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('No-Show Analysis', aMargin, y + 6);
      pdf.text('Attendance Optimization Report', aMargin, y + 14);
      y += 20;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, aMargin, y);
      y += 5;

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(aMargin, y, aPageWidth - aMargin, y);
      y += 8;

      const checkPageBreak = (neededSpace) => {
        if (y + neededSpace > aPageHeight - 35) {
          pdf.addPage('letter', 'portrait');
          y = aMargin;
        }
      };

      const sectionHeader = (title) => {
        checkPageBreak(20);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 58, 138);
        pdf.text(title, aMargin, y);
        y += 9;
      };

      const bulletLine = (text, indent = 4) => {
        checkPageBreak(7);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(40, 40, 40);
        const lines = pdf.splitTextToSize(text, aPageWidth - aMargin * 2 - indent - 4);
        pdf.text(`\u2022  ${lines[0]}`, aMargin + indent, y);
        y += 5;
        for (let i = 1; i < lines.length; i++) {
          checkPageBreak(5);
          pdf.text(`   ${lines[i]}`, aMargin + indent, y);
          y += 5;
        }
      };

      // Practice Summary
      sectionHeader('Practice Attendance Profile');
      bulletLine(`Total Clients Analyzed: ${totalClients}`);
      bulletLine(`Total Sessions: ${totalAppointments}`);
      bulletLine(`Total Missed Appointments: ${totalNoShows}`);
      bulletLine(`Practice No-Show Rate: ${practiceNoShowRate}%`);
      bulletLine(`Current Attendance Rate: ${attendanceRate}%`);
      y += 4;

      // Risk Distribution
      sectionHeader('Risk Distribution');
      bulletLine(`Critical (>50% no-show): ${criticalCount} clients (${criticalPct}% of caseload)`);
      bulletLine(`High Risk (35-50% no-show): ${highRiskCount} clients (${highRiskPct}% of caseload)`);
      bulletLine(`Moderate Risk (25-35%): ${moderateRiskCount} clients`);
      bulletLine(`Low Risk (<25%): ${lowRiskCount} clients`);
      y += 4;

      // Priority Clients
      if (topNoShowClients.length > 0) {
        sectionHeader('Priority Clients for Outreach');
        topNoShowClients.forEach(client => {
          bulletLine(`${client.clientId}: ${client.noShowPct}% no-show rate (${client.noShows} of ${client.totalAppointments} sessions missed)`);
        });
        y += 4;
      }

      // Intervention Summary
      sectionHeader('Intervention Strategies by Risk Level');
      bulletLine('Critical: Immediate outreach within 48h, treatment viability review, crisis assessment, intensive attendance protocol, supervisor consultation');
      bulletLine('High Risk: Attendance agreement, front-loading sessions, multi-modal reminders, session length modification');
      bulletLine('Moderate Risk: Enhanced reminders with confirmation, barrier assessment, schedule optimization, between-session engagement');
      bulletLine('Low Risk: Attendance reinforcement, standard reminders, preferred scheduling, therapeutic alliance focus');
      y += 4;

      // Action Plan
      sectionHeader('Action Plan');
      bulletLine('This Week: Review top no-show clients, schedule direct conversations, assess barriers, verify reminder system');
      bulletLine('This Month: Implement tiered reminders, create attendance agreement template, conduct barrier assessments, optimize scheduling');
      bulletLine('Ongoing: Re-run analysis monthly, reinforce attendance improvements, watch for early warning signs, discuss in supervision');

      pdf.save(`NoShow_Analysis_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('PDF generation failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const hasData = chartData && chartData.chartItems && chartData.chartItems.length > 0;

  return (
    <button
      onClick={generatePDF}
      disabled={generating || !hasData}
      className={`px-6 py-3 rounded-lg font-semibold transition-all ${
        generating
          ? 'bg-gray-300 text-gray-500 cursor-wait'
          : !hasData
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-green-600 text-white hover:bg-green-700 shadow-md'
      }`}
    >
      {generating ? 'Generating PDF...' : 'Download PDF'}
    </button>
  );
};

export default NoShowPDF;
